import React, { useEffect, useState } from 'react';
import '../../css/dashboardStylings/DashboardRemindersStyling.css';

const DashboardRemindersComponent = ({ allUserPlants = [], gardens = [] }) => {
    const [reminders, setReminders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCompleted, setShowCompleted] = useState(false);

    useEffect(() => {
        fetchReminders();
    }, []);

    const fetchReminders = async () => {
        try {
            setLoading(true);
            const response = await fetch('/api/reminder/fetchUserReminders');
            const result = await response.json();

            if (response.ok && result.data) {
                // Sort by reminder_datetime, putting overdue first
                const sortedReminders = result.data.sort((a, b) => {
                    const dateA = new Date(a.reminder_datetime);
                    const dateB = new Date(b.reminder_datetime);
                    const now = new Date();

                    // Overdue reminders first
                    if (dateA < now && dateB >= now) return -1;
                    if (dateB < now && dateA >= now) return 1;

                    return dateA - dateB;
                });
                setReminders(sortedReminders);
            }
        } catch (error) {
            console.error('Error fetching reminders:', error);
            // Fallback with sample data for development
            setReminders([
                {
                    id: 1,
                    title: "Water Tomatoes",
                    description: "Check soil moisture and water if needed",
                    reminder_datetime: "2025-08-07T09:00:00",
                    is_completed: false,
                    priority: 3,
                    tags: ["watering", "vegetables"],
                    plant_id: 1,
                    garden_id: 1
                },
                {
                    id: 2,
                    title: "Fertilize Herbs",
                    description: "Apply organic fertilizer to herb garden",
                    reminder_datetime: "2025-08-08T08:00:00",
                    is_completed: false,
                    priority: 2,
                    tags: ["fertilizing", "herbs"],
                    garden_id: 1
                },
                {
                    id: 3,
                    title: "Harvest Lettuce",
                    description: "Pick outer leaves from lettuce plants",
                    reminder_datetime: "2025-08-06T18:00:00",
                    is_completed: true,
                    priority: 1,
                    tags: ["harvesting", "vegetables"],
                    plant_id: 3
                }
            ]);
        } finally {
            setLoading(false);
        }
    };

    const toggleReminderComplete = async (reminderId) => {
        try {
            const reminder = reminders.find(r => r.id === reminderId);
            const response = await fetch(`/api/reminder/updateReminder/${reminderId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    is_completed: !reminder.is_completed
                }),
            });

            if (response.ok) {
                setReminders(reminders.map(r =>
                    r.id === reminderId
                        ? { ...r, is_completed: !r.is_completed }
                        : r
                ));
            }
        } catch (error) {
            console.error('Error updating reminder:', error);
        }
    };

    const formatDateTime = (dateTimeString) => {
        const date = new Date(dateTimeString);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const reminderDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        const timeString = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        if (reminderDate.getTime() === today.getTime()) {
            return `Today at ${timeString}`;
        } else if (reminderDate.getTime() === today.getTime() - 86400000) {
            return `Yesterday at ${timeString}`;
        } else if (reminderDate.getTime() === today.getTime() + 86400000) {
            return `Tomorrow at ${timeString}`;
        } else {
            return date.toLocaleDateString() + ' at ' + timeString;
        }
    };

    const isOverdue = (dateTimeString) => {
        return new Date(dateTimeString) < new Date();
    };

    const getPriorityIcon = (priority) => {
        switch(priority) {
            case 3: return "🔴";
            case 2: return "🟡";
            case 1: return "🟢";
            default: return "⚪";
        }
    };

    const getPlantName = (plantId) => {
        const plant = allUserPlants.find(p => p.id === plantId);
        return plant ? plant.plant_name || plant.name : null;
    };

    const getGardenName = (gardenId) => {
        const garden = gardens.find(g => g.id === gardenId);
        return garden ? garden.name : null;
    };

    const activeReminders = reminders.filter(r => !r.is_completed);
    const completedReminders = reminders.filter(r => r.is_completed);
    const displayReminders = showCompleted ? completedReminders : activeReminders;

    if (loading) {
        return (
            <div className="reminders-loading">
                <div className="reminder-skeleton"></div>
                <div className="reminder-skeleton"></div>
                <div className="reminder-skeleton"></div>
            </div>
        );
    }

    return (
        <div className="dashboard-reminders">
            <div className="reminders-header">
                <div className="reminder-tabs">
                    <button
                        className={`tab ${!showCompleted ? 'active' : ''}`}
                        onClick={() => setShowCompleted(false)}
                    >
                        Active ({activeReminders.length})
                    </button>
                    <button
                        className={`tab ${showCompleted ? 'active' : ''}`}
                        onClick={() => setShowCompleted(true)}
                    >
                        Completed ({completedReminders.length})
                    </button>
                </div>
            </div>

            <div className="reminders-list">
                {displayReminders.length === 0 ? (
                    <div className="no-entries-message">
                        <div className="no-reminders-icon">
                            {showCompleted ? "✅" : "📅"}
                        </div>
                        <p className={`no-reminders-message-text`}>
                            {showCompleted
                                ? "No completed reminders yet"
                                : "No active reminders - you're all caught up!"
                            }
                        </p>
                    </div>
                ) : (
                    displayReminders.map((reminder) => (
                        <div
                            key={reminder.id}
                            className={`reminder-item ${reminder.is_completed ? 'completed' : ''} ${isOverdue(reminder.reminder_datetime) && !reminder.is_completed ? 'overdue' : ''}`}
                        >
                            <div className="reminder-checkbox">
                                <input
                                    type="checkbox"
                                    checked={reminder.is_completed}
                                    onChange={() => toggleReminderComplete(reminder.id)}
                                    className="custom-checkbox"
                                />
                            </div>

                            <div className="reminder-content">
                                <div className="reminder-header">
                                    <div className="reminder-title">
                                        {getPriorityIcon(reminder.priority)}
                                        <span>{reminder.title}</span>
                                    </div>
                                    <div className={`reminder-time ${isOverdue(reminder.reminder_datetime) && !reminder.is_completed ? 'overdue-text' : ''}`}>
                                        {isOverdue(reminder.reminder_datetime) && !reminder.is_completed && "⚠️ "}
                                        {formatDateTime(reminder.reminder_datetime)}
                                    </div>
                                </div>

                                {reminder.description && (
                                    <div className="reminder-description">
                                        {reminder.description}
                                    </div>
                                )}

                                <div className="reminder-meta">
                                    {getPlantName(reminder.plant_id) && (
                                        <span className="reminder-plant">
                                            🌱 {getPlantName(reminder.plant_id)}
                                        </span>
                                    )}
                                    {getGardenName(reminder.garden_id) && (
                                        <span className="reminder-garden">
                                            🏡 {getGardenName(reminder.garden_id)}
                                        </span>
                                    )}
                                    {reminder.tags && reminder.tags.length > 0 && (
                                        <div className="reminder-tags">
                                            {reminder.tags.map((tag, index) => (
                                                <span key={index} className="reminder-tag">
                                                    #{tag}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {displayReminders.length > 0 && (
                <div className="reminders-footer">
                    <div className={`click-to-view-all-button`}>
                        <div className={`click-to-view-all-text`}>View and edit your reminders</div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DashboardRemindersComponent;